import { api } from "../../shared/api/client";
import type {
  CustomerSearchResponse,
  ExceptionResolveRequest,
  ExceptionResolveResponse,
  ExceptionReason,
  ExceptionStatus,
  PaymentDetailResponse,
  PaymentExceptionDetailResponse,
  PaymentExceptionListResponse,
  PaymentTimelineResponse,
  PaymentTokenResponse,
  RecordPaymentRequest,
  RecordPaymentResponse,
} from "./types";

/**
 * Payments-domain API calls (payment-service, /api/v1 base).
 * Base endpoints per contracts/api/v1/payments.yaml; flow-09 extensions
 * (timeline, exceptions, token regenerate/resend, customer search param)
 * per the TASK-009 contract — see types.ts header.
 */

/** POST /payments — idempotent via the Idempotency-Key header. */
export function recordPayment(body: RecordPaymentRequest, idempotencyKey: string) {
  return api.post<RecordPaymentResponse>("/payments", body, idempotencyKey);
}

export function getPayment(id: number) {
  return api.get<PaymentDetailResponse>(`/payments/${id}`);
}

/**
 * Idempotent token regenerate — replay returns the SAME token; the full
 * token is present in this response (never logged server-side).
 */
export function regenerateToken(paymentId: number, idempotencyKey: string) {
  return api.post<PaymentTokenResponse>(`/payments/${paymentId}/token/regenerate`, {}, idempotencyKey);
}

/** Resend the token / ownership SMS after a delivery failure. */
export function resendTokenSms(paymentId: number) {
  return api.post<PaymentTokenResponse>(`/payments/${paymentId}/token/resend-sms`, {});
}

export function getPaymentTimeline(customerId: number) {
  return api.get<PaymentTimelineResponse>(`/customers/${customerId}/payment-timeline`);
}

/** Customer lookup — extends the existing list endpoint with `search`. */
export function searchCustomers(q: string) {
  const qs = new URLSearchParams({ search: q, per_page: "10" });
  return api.get<CustomerSearchResponse>(`/customers?${qs.toString()}`);
}

export type DaysEstimate = {
  amount: number;
  estimated_days: number;
  fully_paid: boolean;
  daily_rate_tzs: number;
  basis: string;
  message: string;
  arrears_applied_cents?: number;
  activation_cents?: number;
  package?: { id: number; amount: number; days: number; label: string } | null;
  shortfall_cents?: number;
  remaining_for_token_tzs?: number;
  token_eligible?: boolean;
  installment_min_cents?: number;
  installment_credit_after_cents?: number;
};

/** Preview PAYG days for an amount before recording or approving. */
export function estimatePaymentDays(params: {
  amountTzs?: number;
  amountCents?: number;
  customerId?: number;
  contractId?: number;
}) {
  const qs = new URLSearchParams();
  if (params.amountCents != null) qs.set("amount_cents", String(params.amountCents));
  else if (params.amountTzs != null) qs.set("amount", String(params.amountTzs));
  if (params.customerId != null) qs.set("customer_id", String(params.customerId));
  if (params.contractId != null) qs.set("contract_id", String(params.contractId));
  return api.get<DaysEstimate>(`/payments/estimate-days?${qs.toString()}`);
}

// ── Reconciliation exceptions (S6/S7) ─────────────────────────────────────────

export function listPaymentExceptions(params: { status?: ExceptionStatus; reason?: ExceptionReason }) {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  if (params.reason) qs.set("reason", params.reason);
  qs.set("per_page", "100");
  return api.get<PaymentExceptionListResponse>(`/payment-exceptions?${qs.toString()}`);
}

export function getPaymentException(id: number) {
  return api.get<PaymentExceptionDetailResponse>(`/payment-exceptions/${id}`);
}

/** Resolve is idempotent server-side via the Idempotency-Key header. */
export function resolvePaymentException(
  id: number,
  body: ExceptionResolveRequest,
  idempotencyKey: string,
) {
  return api.post<ExceptionResolveResponse>(`/payment-exceptions/${id}/resolve`, body, idempotencyKey);
}
